import React, { useContext, useEffect, useState } from 'react';
import { Button } from "@progress/kendo-react-buttons";
import { SerialContext } from './SerialProvider';
import { useNotificationAPI } from '../../components/NotificationProvider';

export type PortState = "closed" | "closing" | "open" | "opening";

interface Message {
    timestamp: number,
    value: string
}

interface PageSerialProps {
    handleCompletedMessage: (value: string) => void,
    showMessages: boolean,
    children?: React.ReactNode
}

export const SerialPage = (props: PageSerialProps) => {
    const serial = useContext(SerialContext);

    let messages = new Set<Message>();
    let [receivedMessages, setReceivedMessages] = useState(new Array<string>());

    const { addNotification } = useNotificationAPI();

    const handleConnect = async () => {
        const isConnected = await serial.connect();
        if (isConnected) {
            addNotification('Connected to the serial port!', 'Serial Page', 'info');
        } else {
            addNotification('Failed to connect to the serial port. Please make sure you are using Chrome or Edge.', 'Serial Page', 'info');
        }
    };

    const handleDisconnect = () => {
        serial.disconnect();
        addNotification('Disconnected from the serial port.', 'Serial Page', 'info')
    };

    //When messages are being received via the serial port, they might be split into several messages, so we need to wait for the full message before saving the run
    let messageFunctionTimeout = {} as NodeJS.Timeout;
    const handleReceiveMessage = (message: any) => {
        //Remove any pending timeouts so we don't iterate over the same messages times
        clearTimeout(messageFunctionTimeout);

        messages.add(message);
        messageFunctionTimeout = setTimeout(() => {
            let incompleteMessage = "";
            Array.from(messages).sort((first, second) => first.timestamp - second.timestamp).forEach(runMessage => {
                incompleteMessage += runMessage.value;
                const messageArray = incompleteMessage.split(",");

                //'Not Selected' is how the messages end, so if Selected is in there we know we have at least one full message
                if (messageArray.length === 7) {
                    //The measurement is currently the 5th part of the string, that might change with instrument
                    //  Example message: 09-Aug-2024,Laser Liquid,,3:16:42 PM,1.33300,RI,20.00C
                    //  1.33300 is the measurement we are after
                    props.handleCompletedMessage(messageArray[4]);
                    //Save the messages to display on the page in case of issues
                    receivedMessages = receivedMessages.concat(incompleteMessage);
                    setReceivedMessages(receivedMessages);
                    //Clear out the message for another run to happen
                    incompleteMessage = "";
                } else if (messageArray.length > 7) {
                    //This should never happen. If it does we want to know
                    console.log("Corrupted Message: ", messageArray);
                    addNotification('The message has been corrupted. Please leave this page open and contact IT.', 'Serial Page', 'error');
                }
            });
            messages = new Set();
        }, 1000);
    };

    // Subscribe to incoming serial messages
    useEffect(() => {
        const unsubscribe = serial.subscribe(handleReceiveMessage);
        return () => unsubscribe();
    }, [serial]);

    return (
        <div className="header-info-row s-k-flex-50 p-4 rounded-lg streck-secondary">
            {serial.portState === "open" ?
                <>
                    Refractometer: <span style={{ color: 'limegreen' }}>Connected</span>
                    <div className="p-2">
                        <Button onClick={handleDisconnect} themeColor={"primary"}>Disconnect</Button>
                    </div>
                </> :
                <>
                    Refractometer: <span style={{ color: 'red' }}>Disconnected</span>
                    <div className="p-2">
                        <Button onClick={handleConnect} themeColor={"primary"}>Connect</Button>
                    </div>
                </>
            }
            {props.showMessages &&
                <>
                    <br></br><br></br>
                    <h1>Messages:</h1>
                    <br></br><br></br>
                    {receivedMessages.map((_message, index) => (
                        <div className="p-2 border-bottom align-middle" key={index}>{_message}</div>
                    ))}
                </>}
        </div>
    );
}